import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, CheckCircle2Icon, EyeIcon, PauseCircleIcon, PlusIcon, PrinterIcon, SaveIcon, SendIcon, Undo2Icon, XCircleIcon, XIcon, MessageSquarePlusIcon } from 'lucide-react';
import type { AdminRole } from '../../../types/config';
import { useStore } from '../../../contexts/StoreContext';
import { useNoteFiles, type FileDecision } from '../../../hooks/useNoteFiles';
import { Modal } from '../../shared/Modal';
import { StatusBadge } from '../../shared/StatusBadge';
import { DocPreview } from '../../shared/DocPreview';
import { CombinedNoteDoc } from '../../print/CombinedNoteDoc';
import { InfoGrid } from '../InfoGrid';
import { NoteEditor, type NoteDraft } from './NoteEditor';
import { NOTE_FILE_LABEL, availableForNote, categoryLabel, paymentSourceLabel } from '../../../utils/caseStatus';
import { can, DENIED } from '../../../utils/permissions';
import { bn, fmtDate, fmtDateTime, taka } from '../../../utils/bn';
interface Props {
  fileId: string;
  role: AdminRole;
  onBack: () => void;
  openCase: (pcr: string) => void;
}
const DECISION_UI: Record<FileDecision, {
  title: string;
  btn: string;
  cls: string;
  needComment: boolean;
}> = {
  approved: {
    title: 'চূড়ান্ত অনুমোদন',
    btn: 'অনুমোদন করুন',
    cls: 'btn-green',
    needComment: false
  },
  returned: {
    title: 'সংশোধনের জন্য ফেরত',
    btn: 'ফেরত পাঠান',
    cls: 'btn-warning',
    needComment: true
  },
  rejected: {
    title: 'অনুমোদন প্রত্যাখ্যান',
    btn: 'প্রত্যাখ্যান করুন',
    cls: 'btn-danger',
    needComment: true
  },
  held: {
    title: 'নথি স্থগিত রাখুন',
    btn: 'স্থগিত করুন',
    cls: 'btn-outline',
    needComment: true
  }
};
export function NoteFileView({
  fileId,
  role,
  onBack,
  openCase
}: Props) {
  const {
    noteFiles,
    cases,
    config
  } = useStore();
  const nf = useNoteFiles(role);
  const file = noteFiles.find(f => f.id === fileId);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<NoteDraft>({
    subject: '',
    observation: '',
    recommendation: ''
  });
  const [attachInput, setAttachInput] = useState('');
  const [include, setInclude] = useState<Record<string, {
    on: boolean;
    reason: string;
  }>>({});
  const [comment, setComment] = useState('');
  const [decision, setDecision] = useState<FileDecision | null>(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardText, setForwardText] = useState('সদয় অনুমোদনের জন্য পেশ করা হলো।');
  const [addOpen, setAddOpen] = useState(false);
  const [addSel, setAddSel] = useState<string[]>([]);
  const [record, setRecord] = useState<string | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!file) return;
    setDraft({
      subject: file.subject,
      observation: file.observation,
      recommendation: file.recommendation
    });
    setInclude(Object.fromEntries(file.pcrs.map(p => [p, {
      on: true,
      reason: ''
    }])));
  }, [file?.id, file?.savedAt, file?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!file) return <p className="p-6 text-sm" data-id="element-136" data-mp="3d7wyqqn28n6e">নথি পাওয়া যায়নি।</p>;
  const included = file.pcrs.map(p => cases.find(c => c.pcr === p)).filter((c): c is NonNullable<typeof c> => !!c);
  const total = included.reduce((s, c) => s + c.fine + c.extraCharge, 0);
  const editable = can(role, 'note') && (file.status === 'draft' || file.status === 'returned');
  const deciding = can(role, 'approve') && (file.status === 'pending' || file.status === 'held');
  const available = cases.filter(availableForNote);
  const approver = config.approvalLevels.find(l => l.role === 'approver')?.officer ?? 'অনুমোদনকারী';
  const excluded = Object.entries(include).filter(([, v]) => !v.on).map(([pcr, v]) => ({
    pcr,
    reason: v.reason.trim()
  }));
  const recordCase = cases.find(c => c.pcr === record);
  const save = (silent = false) => nf.save(file.id, {
    ...draft,
    bodyHtml: bodyRef.current?.innerHTML ?? file.bodyHtml
  }, silent);
  const confirmDecision = () => {
    if (!decision) return;
    if (DECISION_UI[decision].needComment && !comment.trim()) return setError('এই সিদ্ধান্তের জন্য মন্তব্য আবশ্যক।');
    if (decision === 'approved') {
      if (excluded.length === file.pcrs.length) return setError('অন্তত একটি PCR অনুমোদনে অন্তর্ভুক্ত রাখুন।');
      if (excluded.some(e => !e.reason)) return setError('বাদ দেওয়া প্রতিটি PCR-এর কারণ লিখুন।');
    }
    nf.decide(file.id, decision, comment.trim(), decision === 'approved' ? excluded : []);
    setDecision(null);
    setComment('');
    setError('');
  };
  const steps = [{
    label: 'কাগজপত্র যাচাই',
    who: 'দায়িত্বপ্রাপ্ত কর্মকর্তা',
    state: 'done'
  }, {
    label: 'নোটশিট প্রস্তুত ও অগ্রায়ন',
    who: file.createdBy,
    state: file.status === 'draft' || file.status === 'returned' ? 'current' : 'done'
  }, {
    label: 'চূড়ান্ত অনুমোদন',
    who: approver,
    state: file.status === 'approved' ? 'done' : file.status === 'rejected' ? 'issue' : file.status === 'pending' || file.status === 'held' ? 'current' : 'pending'
  }];
  return <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-137" data-mp="1r87eg6jvzdlm">
        <button className="btn btn-outline" onClick={onBack} data-id="element-138" data-mp="1raa3an9az0or"><ArrowLeftIcon className="h-4 w-4" data-id="element-139" data-mp="1ma5uljemza93" /> নথি তালিকা</button>
        <button className="btn btn-outline" onClick={() => {
        if (editable) save(true);
        setPrintOpen(true);
      }} data-id="element-140" data-mp="216ddmgeb65ms"><PrinterIcon className="h-4 w-4" data-id="element-141" data-mp="1w7aa50dz87gx" /> প্রিভিউ ও প্রিন্ট</button>
      </div>

      <section className="card mb-5 p-5" data-id="element-142" data-mp="1w9mz3xi5ji3h">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-143" data-mp="2a69uhvugi07a">
          <div className="min-w-0" data-id="element-144" data-mp="2588btc0y02wc">
            <p className="text-xs text-ink-muted" data-id="element-145" data-mp="25770r7jxfgem">নথি নম্বর</p>
            <h1 className="text-2xl font-bold text-ink" data-id="element-146" data-mp="2jwlaigrezzuj">{file.fileNo}</h1>
            <p className="mt-1 text-sm text-ink" data-id="element-147" data-mp="2jypd8vdkust8">{file.subject}</p>
          </div>
          <StatusBadge {...NOTE_FILE_LABEL[file.status]} data-id="element-148" data-mp="2eyjs7y1tc4k4" />
        </div>
        <div className="mt-4" data-id="element-149" data-mp="2sykt3di2vbzl">
          <InfoGrid cols={4} items={[['অন্তর্ভুক্ত PCR', `${bn(file.pcrs.length)}টি`], ['ট্রাফিক বিভাগ', file.division], ['মোট জরিমানা', taka(total)], ['সর্বশেষ সংরক্ষণ', fmtDateTime(file.savedAt)]]} data-id="element-150" data-mp="2sxt0mbjs1udj" />
        </div>
        <ol className="mt-5 grid gap-2 sm:grid-cols-3" data-id="element-151" data-mp="30xphju4ciew1">
          {steps.map((s, i) => <li key={s.label} className={`rounded-lg border px-3 py-2 ${s.state === 'current' ? 'border-navy bg-navy-50' : s.state === 'done' ? 'border-success/30 bg-success-50' : s.state === 'issue' ? 'border-danger/30 bg-danger-50' : 'border-line'}`} data-id="element-152" data-mp="35xlmpwos4cd6" data-mpi={i}>
              <p className="text-xs text-ink-muted" data-id="element-153" data-mp="3ep44gc1dj08d">ধাপ {bn(i + 1)}</p>
              <p className="text-sm font-bold text-ink" data-id="element-154" data-mp="3jnp1y3p8r5t4">{s.label}</p>
              <p className="truncate text-xs text-ink-muted" data-id="element-155" data-mp="3oo2zgrt7uxcw">{s.who}</p>
            </li>)}
        </ol>
        {file.status === 'returned' && file.comment && <p className="mt-4 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700" data-id="element-156" data-mp="3tm4v0c7twp59"><b data-id="element-157" data-mp="1uatw7hr9k833">অনুমোদনকারীর নির্দেশনা:</b> {file.comment}</p>}
        {file.status === 'held' && file.comment && <p className="mt-4 rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-700" data-id="element-158" data-mp="1z9ofslx220o9"><b data-id="element-159" data-mp="ge0qo5a9swwl">স্থগিতের কারণ:</b> {file.comment}</p>}
      </section>

      <div className="grid gap-5 xl:grid-cols-[1fr_380px]" data-id="element-160" data-mp="gflny2nyyty5">
        <div className="min-w-0" data-id="element-161" data-mp="bm903ur46r6g">
          {editable ? <NoteEditor file={file} draft={draft} onDraft={setDraft} bodyRef={bodyRef} onRegenerate={() => nf.regenerate(file.id)} data-id="element-162" data-mp="7l75jr8573at" /> : <div className="overflow-x-auto" data-id="element-163" data-mp="2nngrxdtj84m"><CombinedNoteDoc file={file} cases={cases} config={config} printable={false} data-id="element-164" data-mp="2k9ib089zj2g" /></div>}
        </div>

        <aside className="space-y-4" data-id="element-165" data-mp="zaaa783xyfnz">
          <section className="card p-4" data-id="element-166" data-mp="varowb87k2km">
            <div className="mb-2 flex items-center justify-between" data-id="element-167" data-mp="v8x5508oagi4">
              <h2 className="text-sm font-bold text-ink" data-id="element-168" data-mp="qan9koj1ddvz">অন্তর্ভুক্ত PCR ({bn(file.pcrs.length)})</h2>
              {editable && <button className="btn btn-ghost btn-sm" disabled={!available.length} onClick={() => {
              setAddSel([]);
              setAddOpen(true);
            }} data-id="element-169" data-mp="lcc3movv3nj1"><PlusIcon className="h-3.5 w-3.5" data-id="element-170" data-mp="3azt5bhics592" /> PCR যোগ</button>}
            </div>
            {deciding && <p className="mb-2 text-xs text-ink-muted" data-id="element-171" data-mp="365fgxz23yydw">কোনো PCR অনুমোদন থেকে বাদ দিতে টিক উঠিয়ে কারণ লিখুন — বাকিগুলো অনুমোদিত হবে।</p>}
            <ul className="divide-y divide-line" data-id="element-172" data-mp="1ezkfc06auvs8">
              {included.map(c => {
              const inc = include[c.pcr] ?? {
                on: true,
                reason: ''
              };
              return <li key={c.pcr} className="py-2.5" data-id="element-173" data-mp="150lh7bj5uijh">
                    <div className="flex items-start gap-2" data-id="element-174" data-mp="1513hqkcuvix7">
                      {deciding && <input type="checkbox" className="mt-1 h-4 w-4 accent-bdgreen" aria-label={`${c.pcr} অনুমোদনে অন্তর্ভুক্ত`} checked={inc.on} onChange={e => setInclude(s => ({
                    ...s,
                    [c.pcr]: {
                      ...inc,
                      on: e.target.checked
                    }
                  }))} data-id="element-175" data-mp="1srqsdde5omng" />}
                      <div className="min-w-0 flex-1" data-id="element-176" data-mp="1iys2ztau4s2s">
                        <p className={`font-mono text-sm font-semibold ${inc.on ? 'text-navy' : 'text-ink-soft line-through'}`} data-id="element-177" data-mp="1nyehe62eged6">{c.pcr}</p>
                        <p className="truncate text-xs text-ink-muted" data-id="element-178" data-mp="2bngwb65nft3z">{c.regNo} · {categoryLabel(c.category, config)} · {taka(c.fine + c.extraCharge)}</p>
                      </div>
                      <button className="btn btn-ghost h-7 w-7 px-0" title="সহায়ক রেকর্ড" aria-label="সহায়ক রেকর্ড" onClick={() => setRecord(c.pcr)} data-id="element-179" data-mp="4z6tvujuz3g1"><EyeIcon className="h-4 w-4" data-id="element-180" data-mp="equpm7ng76nb" /></button>
                      {editable && <button className="btn btn-ghost h-7 w-7 px-0 text-danger" title="নোট থেকে বাদ দিন" aria-label="নোট থেকে বাদ দিন" onClick={() => nf.removePcr(file.id, c.pcr)} data-id="element-181" data-mp="eq1iv6ohoh56"><XIcon className="h-4 w-4" data-id="element-182" data-mp="3i6rsrwn6zec5" /></button>}
                    </div>
                    {deciding && !inc.on && <input className="input mt-2 h-9" placeholder="বাদ দেওয়ার কারণ" value={inc.reason} onChange={e => setInclude(s => ({
                  ...s,
                  [c.pcr]: {
                    ...inc,
                    reason: e.target.value
                  }
                }))} data-id="element-183" data-mp="3dab3gz49atxh" />}
                  </li>;
            })}
            </ul>
            {file.excluded.length > 0 && <p className="mt-2 text-xs text-danger-700" data-id="element-184" data-mp="16jv75sf8eebx">পূর্বে বাদ: {file.excluded.map(e => e.pcr).join(', ')}</p>}
          </section>

          {editable && <section className="card p-4" data-id="element-185" data-mp="1q5s8inpo7tm3">
              <h2 className="mb-2 text-sm font-bold text-ink" data-id="element-186" data-mp="1m4orvyzsttjt">সংযুক্তি</h2>
              <ul className="mb-2 space-y-1" data-id="element-187" data-mp="1m6t7he728gzx">
                {file.attachments.map(a => <li key={a} className="flex items-center justify-between gap-2 rounded bg-canvas px-2 py-1 text-xs" data-id="element-188" data-mp="1hc5cqxlgqqcw">
                    <span className="truncate" data-id="element-189" data-mp="1cd2i873402j6">{a}</span>
                    <button className="text-ink-soft hover:text-danger" aria-label="সংযুক্তি মুছুন" onClick={() => nf.save(file.id, {
                attachments: file.attachments.filter(x => x !== a)
              }, true)} data-id="element-190" data-mp="17glliohugfuh"><XIcon className="h-3.5 w-3.5" data-id="element-191" data-mp="yecdq6dyn0ks" /></button>
                  </li>)}
              </ul>
              <div className="flex gap-2" data-id="element-192" data-mp="thcg8jv6f65a">
                <input className="input h-9" placeholder="সংযুক্তির নাম" value={attachInput} onChange={e => setAttachInput(e.target.value)} data-id="element-193" data-mp="2st56lxngld91" />
                <button className="btn btn-outline btn-sm h-9" disabled={!attachInput.trim()} onClick={() => {
              nf.save(file.id, {
                attachments: [...file.attachments, attachInput.trim()]
              }, true);
              setAttachInput('');
            }} data-id="element-194" data-mp="2nv3q9khf3s19">যোগ</button>
              </div>
            </section>}

          <section className="card p-4" data-id="element-195" data-mp="352uwjjv31doo">
            <h2 className="mb-3 text-sm font-bold text-ink" data-id="element-196" data-mp="35468w82lftre">কার্যক্রম</h2>
            {editable && <div className="grid gap-2" data-id="element-197" data-mp="3a1o7b4iziql5">
                <button className="btn btn-outline" onClick={() => save()} data-id="element-198" data-mp="2cf8r062anyz6"><SaveIcon className="h-4 w-4" data-id="element-199" data-mp="2cgjx4cj7pitg" /> খসড়া সংরক্ষণ</button>
                <button className="btn btn-primary" onClick={() => {
              save(true);
              setForwardOpen(true);
            }} data-id="element-200" data-mp="2held2olcd20p"><SendIcon className="h-4 w-4" data-id="element-201" data-mp="2mbtx6uq1vg4l" /> {file.status === 'returned' ? 'সংশোধিত নথি পুনঃঅগ্রায়ন' : 'অনুমোদনের জন্য অগ্রায়ন'}</button>
              </div>}
            {deciding && <div className="grid gap-2" data-id="element-202" data-mp="1lmg94g06lw2">
                <label className="label mb-0" htmlFor="nf-c" data-id="element-203" data-mp="1p0h2890qn1e">অনুমোদনকারীর মন্তব্য</label>
                <textarea id="nf-c" rows={3} className="textarea" placeholder="যেমন: তফসিলভুক্ত যানবাহনসমূহ অবমুক্তির অনুমোদন প্রদান করা হলো।" value={comment} onChange={e => setComment(e.target.value)} data-id="element-204" data-mp="5qca6nr3pqec" />
                <button className="btn btn-green" onClick={() => {
              setError('');
              setDecision('approved');
            }} data-id="element-205" data-mp="1xrwx60fnm62c"><CheckCircle2Icon className="h-4 w-4" data-id="element-206" data-mp="2bnhdco400a62" /> চূড়ান্ত অনুমোদন ({bn(file.pcrs.length - excluded.length)}টি PCR)</button>
                <div className="grid grid-cols-3 gap-2" data-id="element-207" data-mp="1j0mv7xunlmvr">
                  <button className="btn btn-warning btn-sm" onClick={() => {
                setError('');
                setDecision('returned');
              }} data-id="element-208" data-mp="1j1630lqlauw2"><Undo2Icon className="h-3.5 w-3.5" data-id="element-209" data-mp="1f15nexrr8dnf" /> ফেরত</button>
                  <button className="btn btn-danger btn-sm" onClick={() => {
                setError('');
                setDecision('rejected');
              }} data-id="element-210" data-mp="1srr8mt4okzoh"><XCircleIcon className="h-3.5 w-3.5" data-id="element-211" data-mp="1nyy4a6d4jtcx" /> প্রত্যাখ্যান</button>
                  <button className="btn btn-outline btn-sm" disabled={file.status === 'held'} onClick={() => {
                setError('');
                setDecision('held');
              }} data-id="element-212" data-mp="109wq02hm1nzq"><PauseCircleIcon className="h-3.5 w-3.5" data-id="element-213" data-mp="vak6nrpy6z1w" /> স্থগিত</button>
                </div>
                <button className="btn btn-ghost btn-sm" disabled={!comment.trim()} onClick={() => {
              nf.comment(file.id, comment.trim());
              setComment('');
            }} data-id="element-214" data-mp="36xvsg3oqdkrp"><MessageSquarePlusIcon className="h-3.5 w-3.5" data-id="element-215" data-mp="3br8gocphzpjx" /> শুধু মন্তব্য সংযুক্ত করুন</button>
              </div>}
            {!editable && !deciding && <p className="text-sm text-ink-muted" data-id="element-216" data-mp="32xbuwz08wf51">
                {file.status === 'pending' ? `নথিটি ${approver}-এর নিকট অনুমোদনের অপেক্ষায়।` : file.status === 'approved' ? 'চূড়ান্ত অনুমোদিত — অন্তর্ভুক্ত PCR-সমূহ অবমুক্তি পত্র প্রস্তুতের জন্য প্রস্তুত।' : file.status === 'rejected' ? 'অনুমোদন প্রত্যাখ্যাত হয়েছে।' : 'এই নথিতে আপনার ভূমিকার জন্য কোনো কার্যক্রম নেই।'}
                {!can(role, 'note') && !can(role, 'approve') && <span className="mt-1 block text-xs" data-id="element-217" data-mp="3lpebybgstxft">{DENIED}</span>}
              </p>}
          </section>

          <section className="card p-4" data-id="element-218" data-mp="2a84tns9epbg3">
            <h2 className="mb-3 text-sm font-bold text-ink" data-id="element-219" data-mp="2a9zjnkzjbtr1">অগ্রায়ন ও মন্তব্যের ইতিহাস</h2>
            {file.forwards.length === 0 && <p className="text-sm text-ink-muted" data-id="element-220" data-mp="2pk5z0rqy96sn">এখনো অগ্রায়ন করা হয়নি।</p>}
            <ol className="relative space-y-3 border-l-2 border-navy-100 pl-4" data-id="element-221" data-mp="2kps0whkwklka">
              {file.forwards.map((f, i) => <li key={i} className="relative" data-id="element-222" data-mp="2gndejni0z3o9" data-mpi={i}>
                  <span className="absolute -left-[23px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-navy" data-id="element-223" data-mp="2brpq5abdaejt" />
                  <p className="text-sm font-semibold text-ink" data-id="element-224" data-mp="38g4fucekfikd">{f.action}</p>
                  <p className="text-xs text-ink-muted" data-id="element-225" data-mp="34gn8tejhqdd3">“{f.remark}”</p>
                  <p className="text-[11px] text-ink-soft" data-id="element-226" data-mp="2zfpr132e7lc4">{f.from} → {f.to} · {fmtDateTime(f.at)}</p>
                </li>)}
            </ol>
          </section>
        </aside>
      </div>

      <Modal open={!!decision} onClose={() => setDecision(null)} title={decision ? DECISION_UI[decision].title : ''} size="md" footer={decision && <><button className="btn btn-outline" onClick={() => setDecision(null)} data-id="element-228" data-mp="1j0o7v0cdo2w2">বাতিল</button><button className={`btn ${DECISION_UI[decision].cls}`} onClick={confirmDecision} data-id="element-229" data-mp="18zdklw6qk5sq">{DECISION_UI[decision].btn}</button></>} data-id="element-227" data-mp="1o1lov9lniqvz">
        {decision === 'approved' ? <div className="space-y-2 text-sm" data-id="element-230" data-mp="1ivozmtxpn9qv">
            <p data-id="element-231" data-mp="1iuxc86e9oibo">নোটশিট <b data-id="element-232" data-mp="1iu5g8ugrcikv">{file.fileNo}</b>-এর মাধ্যমে <b data-id="element-233" data-mp="1nmovrwz09nsb">{bn(file.pcrs.length - excluded.length)}টি PCR</b> চূড়ান্তভাবে অবমুক্তির জন্য অনুমোদিত হবে। এরপর আর কোনো পৃথক অনুমোদন ধাপ নেই — কেসগুলো সরাসরি অবমুক্তি পত্র প্রস্তুতের তালিকায় যাবে।</p>
            {excluded.length > 0 && <p className="rounded-lg bg-warning-50 px-3 py-2 text-warning-700" data-id="element-234" data-mp="1wmlp4bwcbatp">বাদ যাবে: {excluded.map(e => `${e.pcr}${e.reason ? ` (${e.reason})` : ''}`).join('; ')}</p>}
          </div> : <div className="text-sm" data-id="element-235" data-mp="26krk09jqbgp2">
            <p className="mb-2" data-id="element-236" data-mp="26jq8y52pqu7c">সম্পূর্ণ নথি ({bn(file.pcrs.length)}টি PCR) এই সিদ্ধান্তের আওতায় আসবে।</p>
            <textarea rows={3} className="textarea" aria-label="মন্তব্য" placeholder="মন্তব্য / নির্দেশনা (আবশ্যক)" value={comment} onChange={e => setComment(e.target.value)} data-id="element-237" data-mp="7b1nxrzmtovu" />
          </div>}
        {error && <p className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700" role="alert" data-id="element-238" data-mp="3vdeuvopr7f2z">{error}</p>}
      </Modal>

      <Modal open={forwardOpen} onClose={() => setForwardOpen(false)} title="অনুমোদনের জন্য অগ্রায়ন" size="md" footer={<><button className="btn btn-outline" onClick={() => setForwardOpen(false)} data-id="element-240" data-mp="95dpe4adrgxs">বাতিল</button><button className="btn btn-primary" onClick={() => {
        nf.forward(file.id, forwardText.trim());
        setForwardOpen(false);
      }} data-id="element-241" data-mp="n4wq4c0v3q02"><SendIcon className="h-4 w-4" data-id="element-242" data-mp="i6bxaxnxw8ln" /> অগ্রায়ন করুন</button></>} data-id="element-239" data-mp="e5jo24f2i3pd">
        <p className="mb-3 text-sm text-ink" data-id="element-243" data-mp="ww13j7aw32ss">প্রাপক: <b data-id="element-244" data-mp="wxuayotbtr1u">{approver}</b> (চূড়ান্ত অনুমোদনকারী) · {bn(file.pcrs.length)}টি PCR</p>
        <label className="label" htmlFor="fw-t" data-id="element-245" data-mp="rx6l46gu3g97">অগ্রায়ন মন্তব্য</label>
        <textarea id="fw-t" rows={3} className="textarea" value={forwardText} onChange={e => setForwardText(e.target.value)} data-id="element-246" data-mp="15vo2lmg5bwio" />
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="নোটশিটে PCR যোগ করুন" size="md" footer={<><button className="btn btn-outline" onClick={() => setAddOpen(false)} data-id="element-248" data-mp="1khbslo915v9g">বাতিল</button><button className="btn btn-primary" disabled={!addSel.length} onClick={() => {
        nf.addPcrs(file.id, addSel);
        setAddOpen(false);
      }} data-id="element-249" data-mp="1phg758qdofx1">{bn(addSel.length)}টি যোগ করুন</button></>} data-id="element-247" data-mp="11srifrayjscm">
        <ul className="divide-y divide-line rounded-lg border border-line" data-id="element-250" data-mp="1gfzxj37e0htv">
          {available.map(c => <li key={c.pcr} data-id="element-251" data-mp="11rc7fcgb90bb">
              <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5" data-id="element-252" data-mp="16jdf0l2ja2v0">
                <input type="checkbox" className="h-4 w-4 accent-navy" checked={addSel.includes(c.pcr)} onChange={e => setAddSel(s => e.target.checked ? [...s, c.pcr] : s.filter(p => p !== c.pcr))} data-id="element-253" data-mp="16l82o756w92s" />
                <span className="flex-1" data-id="element-254" data-mp="itsu3wmtkvj3"><span className="font-mono text-sm font-semibold text-navy" data-id="element-255" data-mp="it9eimkn6j88">{c.pcr}</span><span className="block text-xs text-ink-muted" data-id="element-256" data-mp="nsw0bkejd7sn">{c.regNo} · {c.division}</span></span>
              </label>
            </li>)}
        </ul>
      </Modal>

      <Modal open={!!recordCase} onClose={() => setRecord(null)} title={`সহায়ক রেকর্ড — ${recordCase?.pcr ?? ''}`} size="lg" footer={recordCase && <button className="btn btn-outline" onClick={() => openCase(recordCase.pcr)} data-id="element-258" data-mp="u9pbuu6clyte">সম্পূর্ণ কেস খুলুন</button>} data-id="element-257" data-mp="13a3zw1ap5s0l">
        {recordCase && <>
            <InfoGrid items={[['যানবাহন', `${recordCase.regNo} · ${categoryLabel(recordCase.category, config)}`], ['মালিক', recordCase.ownerName], ['আটকের কারণ', recordCase.reason], ['আটকের তারিখ', fmtDate(recordCase.createdAt)], ['পেমেন্ট', `${taka(recordCase.fine + recordCase.extraCharge)} · ${paymentSourceLabel(recordCase).label}`], ['লেনদেন / চালান', recordCase.payment.txnId ?? recordCase.payment.challan], ['যাচাইকারী', recordCase.verification?.officer], ['যাচাইয়ের মন্তব্য', recordCase.verification?.remarks], ['স্টেশন / স্লট', `${recordCase.station?.station ?? recordCase.assignedStation} · ${recordCase.station?.slot ?? '—'}`]]} data-id="element-259" data-mp="pd8erah68a46" />
            <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-260" data-mp="28bwkq6dz4ynt">
              {recordCase.documents.map(d => <div key={d.key} data-id="element-261" data-mp="2i7zyk9i6ulql">
                  <DocPreview name={d.name} ownerName={recordCase.ownerName} regNo={recordCase.regNo} fileName={d.fileName} fileType={d.fileType} data-id="element-262" data-mp="2i7q70hhhw6is" />
                  <p className="mt-1 text-xs font-semibold text-ink" data-id="element-263" data-mp="2m7h2gyhyvatn">{d.name}</p>
                </div>)}
            </div>
          </>}
      </Modal>

      <Modal open={printOpen} onClose={() => setPrintOpen(false)} title={`নোটশিট — ${file.fileNo}`} size="xl" footer={<button className="btn btn-primary" onClick={() => window.print()} data-id="element-265" data-mp="1yifokvqd77gz"><PrinterIcon className="h-4 w-4" data-id="element-266" data-mp="23az25sp1y2ls" /> প্রিন্ট</button>} data-id="element-264" data-mp="1uj6zi469rxrr">
        <CombinedNoteDoc file={file} cases={cases} config={config} data-id="element-267" data-mp="23cjwpibd9ljn" />
      </Modal>
    </>;
}